"use client";

import { useEffect, useRef, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { UserCheck, Cpu, Rocket, ArrowRight } from "lucide-react";
import Link from "next/link";
import { Button } from "@/components/ui/button";

export function HowItWorks() {
  const containerRef = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
        }
      },
      { threshold: 0.2 }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const steps = [
    {
      num: "01",
      title: "Build or Import Your Profile",
      description:
        "Upload an existing PDF resume or import LinkedIn details. Our AI instantly parses your skills, experience, and education.",
      icon: UserCheck,
      color: "from-[#FF6B3D] to-[#FF6B3D]/70",
      badgeText: "Instant Import",
    },
    {
      num: "02",
      title: "AI Skill & ATS Optimization",
      description:
        "Run ATS compatibility checks against real job descriptions, discover skill gaps, and receive tailored keyword suggestions.",
      icon: Cpu,
      color: "from-[#14B8A6] to-[#14B8A6]/70",
      badgeText: "Real-Time Scoring",
    },
    {
      num: "03",
      title: "Practice & Land Your Role",
      description:
        "Practice real-time voice interviews with instant clarity scoring, match jobs across Ethiopian job boards, and track applications.",
      icon: Rocket,
      color: "from-[#4B3FD1] to-[#4B3FD1]/70",
      badgeText: "Interview-Ready",
    },
  ];

  return (
    <section
      ref={containerRef}
      className="relative overflow-hidden border-b border-border bg-gradient-to-b from-background via-muted/20 to-background px-6 py-20 md:px-12 md:py-32"
    >
      <div className="mx-auto max-w-6xl">
        {/* Header */}
        <div className="mb-12 text-center">
          <h2 className="mb-4 text-balance text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl md:text-5xl">
            How Career Loop accelerates your growth
          </h2>
          <p className="mx-auto max-w-2xl text-pretty text-base text-muted-foreground sm:text-lg leading-relaxed">
            From initial resume creation to real-time interview practice and job application tracking.
          </p>
        </div>

        {/* Steps Grid */}
        <div className="grid gap-8 md:grid-cols-3 relative">
          {steps.map((step, index) => {
            const Icon = step.icon;
            return (
              <div
                key={step.num}
                className="relative flex flex-col justify-between rounded-2xl border border-border/80 bg-card p-6 shadow-lg backdrop-blur-xl transition-all duration-700 hover:border-[#FF6B3D]/40 hover:shadow-xl"
                style={{
                  transform: inView ? "translateY(0)" : "translateY(30px)",
                  opacity: inView ? 1 : 0,
                  transitionDelay: `${index * 150}ms`,
                }}
              >
                <div>
                  {/* Step Header */}
                  <div className="flex items-center justify-between mb-6">
                    <div
                      className={`flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br ${step.color} text-white shadow-md`}
                    >
                      <Icon className="h-6 w-6" />
                    </div>
                    <span className="text-3xl font-black text-muted-foreground/30">
                      {step.num}
                    </span>
                  </div>

                  <h3 className="mb-3 text-xl font-bold text-foreground">
                    {step.title}
                  </h3>

                  <p className="text-sm leading-relaxed text-muted-foreground mb-6">
                    {step.description}
                  </p>
                </div>

                <Badge
                  variant="secondary"
                  className="w-fit text-xs font-semibold py-1 px-3"
                >
                  {step.badgeText}
                </Badge>
              </div>
            );
          })}
        </div>

        {/* Bottom CTA Link */}
        <div className="mt-16 text-center">
          <Link href="/login">
            <Button
              size="lg"
              className="gap-2 font-bold bg-[#FF6B3D] text-white hover:bg-[#FF6B3D]/90 shadow-lg shadow-[#FF6B3D]/20 hover:scale-105 transition-all"
            >
              <span>Get Started in 60 Seconds</span>
              <ArrowRight className="h-4 w-4" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
}
